import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dbg, dbgErr } from '../lib/debug';

export default function Landing() {
  const { user, loading } = useAuth();
  const [samples, setSamples] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) return;
    dbg('page', 'Landing mounted — fetching /api/samples + /api/site/stats');
    (async () => {
      try {
        const res = await api.get('/api/samples');
        const mixed = [
          ...(res.dissertations || []).map((s) => ({ ...s, kind: 'dissertation' })),
          ...(res.assignments || []).map((s) => ({ ...s, kind: 'assignment' })),
          ...(res.timeseries || []).map((s) => ({ ...s, kind: 'timeseries' })),
        ];
        mixed.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        setSamples(mixed);
      } catch (e) {
        dbgErr('err', 'Landing samples fetch failed', e);
      }
    })();
    (async () => {
      try {
        const res = await api.get('/api/site/stats');
        setStats(res);
      } catch (e) {
        dbgErr('err', 'Landing stats fetch failed', e);
      }
    })();
  }, [user]);

  if (loading) return <div className="center" style={{ padding: '4rem 0' }}>Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;

  // Show up to 4 real samples across all three types; pad with placeholders
  // if the sample user has fewer than 4 entries overall.
  const showcase = padSamples(samples, 4);
  // withFloor: show the real number when it exceeds the marketing floor,
  // otherwise show the floor so early-stage counts never look empty.
  const withFloor = (actual, floor) =>
    typeof actual === 'number' && actual > floor ? actual : floor;
  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');

  return (
    <>
      <section
        style={{
          padding: '3.2rem 0 2.4rem',
          background: 'var(--canvas)',
          borderBottom: '1px solid var(--rule)',
        }}
      >
        <div className="container" style={{ maxWidth: 1180 }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-6">
              <span className="pill mb-3 d-inline-flex align-items-center gap-2">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#10b981',
                    boxShadow: '0 0 8px #10b981',
                  }}
                />
                <span className="font-mono" style={{ fontSize: '0.7rem' }}>
                  {stats ? `${fmt(withFloor(stats.completed_dissertations, 158))} live in the library` : 'Live'}
                </span>
              </span>
              <h1
                style={{
                  fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
                  fontWeight: 800,
                  lineHeight: 1.02,
                  letterSpacing: '-0.03em',
                  color: 'var(--ink)',
                  margin: '0 0 0.8rem',
                }}
              >
                A five-chapter dissertation,
                <br />
                <span
                  className="font-serif-italic"
                  style={{ fontWeight: 400, color: 'var(--primary)' }}
                >
                  written for you.
                </span>
              </h1>
              <p
                style={{
                  fontSize: '1.08rem',
                  color: 'var(--muted)',
                  lineHeight: 1.55,
                  maxWidth: 520,
                  margin: '0 0 1.4rem',
                }}
              >
                Real data analysis, APA references, figures and tables — drafted in minutes,
                exported to PDF or Word. Pay per run.
              </p>
              <div className="d-flex gap-2 flex-wrap">
                <Link to="/register" className="btn btn-cta" style={{ padding: '12px 26px' }}>
                  Start writing
                  <i className="fas fa-arrow-right ms-2" style={{ fontSize: '0.8rem' }}></i>
                </Link>
                <Link
                  to="/samples"
                  className="btn btn-outline-primary"
                  style={{ padding: '12px 22px' }}
                >
                  See samples
                </Link>
              </div>
            </div>
            <div className="col-lg-6 d-none d-lg-block">
              <img
                src="https://images.unsplash.com/photo-1456324504439-367cee3b3c32?auto=format&fit=crop&w=900&q=70"
                alt="Student writing a dissertation"
                style={{
                  width: '100%',
                  height: 360,
                  objectFit: 'cover',
                  borderRadius: 14,
                  boxShadow: '0 18px 40px rgba(10, 11, 20, 0.18)',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '2.2rem 0', background: 'var(--surface)', borderBottom: '1px solid var(--rule)' }}>
        <div className="container" style={{ maxWidth: 1180 }}>
          <div className="row g-3 text-center">
            <Stat value={fmt(withFloor(stats?.completed_dissertations, 158))} label="Dissertations completed" />
            <Stat value={fmt(withFloor(stats?.total_chapters, 790))} label="Chapters drafted" />
            <Stat value={fmt(withFloor(stats?.total_figures, 1264))} label="Figures &amp; tables" />
            <Stat value={fmt(withFloor(stats?.total_worldbank_analyses, 167))} label="Data analyses" />
            <Stat value={fmt(withFloor(stats?.total_users, 223))} label="Researchers on board" />
            <Stat value={fmt(withFloor(stats?.total_words, 2687450))} label="Words written" />
          </div>
          {stats?.countries_covered?.length > 0 && (
            <div className="text-center mt-4" style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
              <span className="font-mono" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: 'var(--muted-2)' }}>
                COUNTRIES ANALYSED —{' '}
              </span>
              {stats.countries_covered.join(' · ')}
            </div>
          )}
        </div>
      </section>

      <section style={{ padding: '3rem 0', background: 'var(--canvas)' }}>
        <div className="container" style={{ maxWidth: 1180 }}>
          <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap gap-2">
            <h2 style={{ fontSize: 'clamp(1.4rem, 2.6vw, 2rem)', fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>
              Recently written
            </h2>
            <Link to="/samples" className="link-underline" style={{ fontSize: '0.9rem' }}>
              Browse all <i className="fas fa-arrow-right ms-1" style={{ fontSize: '0.7rem' }}></i>
            </Link>
          </div>
          <div className="row g-3">
            {showcase.map((s, i) => (
              <SampleCard key={s.id || `placeholder-${i}`} sample={s} imageIdx={i} />
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '3rem 0 4rem', background: 'var(--surface)', borderTop: '1px solid var(--rule)' }}>
        <div className="container" style={{ maxWidth: 1180 }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-7">
              <h3
                className="font-serif"
                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontStyle: 'italic', fontWeight: 400, margin: 0 }}
              >
                Start this afternoon.
              </h3>
              <p className="text-muted mb-0 mt-2" style={{ maxWidth: 520 }}>
                Create an account, pick a credit plan, and generate your first draft.
              </p>
            </div>
            <div className="col-lg-5 text-lg-end">
              <div className="d-flex gap-2 flex-wrap justify-content-lg-end">
                <Link to="/register" className="btn btn-cta" style={{ padding: '12px 26px' }}>
                  Create account
                </Link>
                <Link to="/pricing" className="btn btn-outline-primary" style={{ padding: '12px 22px' }}>
                  See pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ value, label }) {
  return (
    <div className="col-md-2 col-4">
      <div
        className="font-serif"
        style={{
          fontSize: 'clamp(1.4rem, 2.4vw, 2rem)',
          fontWeight: 400,
          color: 'var(--ink)',
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
        }}
      >
        {value}
      </div>
      <div className="text-muted mt-1" style={{ fontSize: '0.78rem', letterSpacing: '0.02em' }}>
        {label}
      </div>
    </div>
  );
}

const SAMPLE_IMAGES = [
  'https://images.unsplash.com/photo-1507668077129-56e32842fceb?auto=format&fit=crop&w=600&q=70',
  'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=600&q=70',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=70',
  'https://images.unsplash.com/photo-1518133910546-b6c2fb7d79e3?auto=format&fit=crop&w=600&q=70',
];

const PLACEHOLDER_SAMPLES = [
  {
    kind: 'dissertation',
    topic: 'Determinants of youth unemployment in sub-Saharan Africa: a mixed-methods study',
    research_area: 'Development economics',
    chapter_count: 5,
    placeholder: true,
  },
  {
    kind: 'assignment',
    topic: 'Leadership styles and employee motivation: a critical essay on Mwaiseni Enterprises',
    assignment_type: 'Essay',
    word_count: 2500,
    placeholder: true,
  },
  {
    kind: 'timeseries',
    topic: 'GDP per capita vs CO2 emissions — Zambia',
    var1_name: 'GDP per capita',
    var2_name: 'CO2 emissions',
    country_name: 'Zambia',
    start_year: 1985,
    end_year: 2024,
    placeholder: true,
  },
  {
    kind: 'dissertation',
    topic: 'The impact of mobile-money adoption on household saving behaviour in Zambia',
    research_area: 'Finance',
    chapter_count: 5,
    placeholder: true,
  },
];

const KIND_META = {
  dissertation: { label: 'Dissertation', icon: 'fa-book', color: 'var(--primary)' },
  assignment:   { label: 'Assignment',   icon: 'fa-file-alt', color: '#f59e0b' },
  timeseries:   { label: 'Time series',  icon: 'fa-chart-line', color: '#10b981' },
};

function padSamples(real, minCount) {
  const out = (real || []).slice(0, minCount);
  let i = 0;
  while (out.length < minCount && i < PLACEHOLDER_SAMPLES.length) {
    out.push(PLACEHOLDER_SAMPLES[i]);
    i += 1;
  }
  return out;
}

function SampleCard({ sample, imageIdx }) {
  const kind = sample.kind || 'dissertation';
  const meta = KIND_META[kind] || KIND_META.dissertation;

  // Secondary chips (type-specific facts). Rendered after the type chip.
  const chips = [];
  if (kind === 'dissertation') {
    if (sample.research_area) chips.push(sample.research_area);
    chips.push(`${sample.chapter_count || 5} chapters`);
  } else if (kind === 'assignment') {
    if (sample.assignment_type) chips.push(sample.assignment_type);
    if (sample.word_count) chips.push(`${Number(sample.word_count).toLocaleString()} words`);
  } else if (kind === 'timeseries') {
    if (sample.country_name) chips.push(sample.country_name);
    if (sample.start_year && sample.end_year) chips.push(`${sample.start_year}–${sample.end_year}`);
  }
  if (sample.created_at) chips.push(new Date(sample.created_at).toLocaleDateString());

  // Title: dissertations/assignments use topic; timeseries synthesises from variables.
  const title = (() => {
    if (kind === 'timeseries') {
      const v1 = sample.var1_name || 'Variable 1';
      const v2 = sample.var2_name || 'Variable 2';
      return `${v1} vs ${v2}`;
    }
    return sample.topic || 'Untitled';
  })();

  // Placeholder cards point to /samples; real cards point to the proper viewer.
  const href = (() => {
    if (sample.placeholder || !sample.id) return '/samples';
    if (kind === 'assignment') return `/assignment/${sample.id}`;
    if (kind === 'timeseries') return `/worldbank/${sample.id}`;
    return `/dissertation/${sample.id}`;
  })();

  return (
    <div className="col-md-3 col-sm-6">
      <Link
        to={href}
        className="card hover-lift h-100 text-decoration-none text-body"
        style={{ overflow: 'hidden', display: 'block' }}
      >
        <img
          src={SAMPLE_IMAGES[imageIdx % SAMPLE_IMAGES.length]}
          alt=""
          style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
        />
        <div style={{ padding: '1rem 1.1rem 1.2rem' }}>
          <div className="d-flex flex-wrap gap-1 mb-2 align-items-center">
            <span
              className="pill"
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#fff',
                background: meta.color,
                border: `1px solid ${meta.color}`,
              }}
            >
              <i className={`fas ${meta.icon} me-1`} style={{ fontSize: '0.65rem' }}></i>
              {meta.label}
            </span>
            {chips.map((c) => (
              <span key={c} className="pill" style={{ fontSize: '0.7rem' }}>
                {c}
              </span>
            ))}
          </div>
          <div
            className="font-serif"
            style={{
              fontSize: '1.05rem',
              fontWeight: 400,
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
              margin: '0 0 0.6rem',
            }}
          >
            {title}
          </div>
          <div
            className="d-flex align-items-center fw-semibold"
            style={{ color: 'var(--ink)', fontSize: '0.82rem' }}
          >
            Read sample
            <i className="fas fa-arrow-right ms-2" style={{ fontSize: '0.68rem' }}></i>
          </div>
        </div>
      </Link>
    </div>
  );
}
